import React from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import { CurrencyRef } from "../../../../models/WorkshopDTO";
import { montoBonito } from "../../../../utils/monto";
//Eventos usa las piezas wau-* de Actualizaciones y sus propios estilos (wev-*), sin depender de los archivos de Talleres.
import "../enrollmentUpdates/styles.css";
import "./eventos.css";
export { errorText } from "../enrollmentRequests/helpers";
export { PromptModal } from "../enrollmentUpdates/piezas";

/** Plantel de la barra (mismo criterio que Matrículas y Actualizaciones). */
export const usePlantel = (): { campusId: number | null; campusName: string; currency: CurrencyRef | null } => {
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const cur: any = legacyCampus?.company?.currency || null;
	return { campusId: legacyCampus?.id ?? sessionCampus?.id ?? null, campusName: legacyCampus?.name || "",
		currency: cur && cur.code ? { id: cur.id, code: cur.code, symbol: cur.symbol } : null };
};

export const dinero = (cur: { symbol?: string | null } | null | undefined, v: any): string => `${cur?.symbol || ""} ${montoBonito(v)}`.trim();
/** Etiqueta con el código de la moneda: "Paquete Graduada (LPS)". */
export const conMoneda = (label: string, cur: CurrencyRef | null | undefined): string => (cur?.code ? `${label} (${cur.code})` : label);

/** Roles que cobran un precio distinto o marcan pagado a mano (EventService::MONEY_ROLE_IDS); superAdmin en todos los planteles. */
export const MONEY_ROLE_IDS = [9, 10, 11, 19, 20, 30, 41, 43];
export const useRolDinero = (campusId: number | null): boolean => {
	const usuario: any = useSelector((state: RootState) => state.userProfile.value);
	const rol: any = usuario?.roleMap?.[usuario?.currentRole];
	if (!rol) return false;
	if (rol.id === 0) return true;
	return MONEY_ROLE_IDS.includes(Number(rol.id)) && (rol.campuses || []).some((c: any) => c.id === campusId);
};

/* "14:30" -> "2:30 pm" */
export const horaBonita = (hhmm: string | null | undefined): string => {
	const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
	if (!m) return "";
	const h = Number(m[1]);
	return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "am" : "pm"}`;
};

const DIAS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
/** "2026-11-29 13:00:00" -> "Dom 29/11/2026 1:00 pm" (sin pasar por UTC). */
export const fechaEvento = (dt: string | null | undefined, conDia = true): string => {
	const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(String(dt || ""));
	if (!m) return "";
	const dia = DIAS[new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay()];
	const hora = m[4] && !(m[4] === "00" && m[5] === "00") ? " " + horaBonita(`${m[4]}:${m[5]}`) : "";
	return `${conDia ? dia + " " : ""}${m[3]}/${m[2]}/${m[1]}${hora}`;
};

const dos = (n: number) => String(n).padStart(2, "0");
/** ISO en UTC -> hora del navegador: "29/11/2026 4:12 pm"; solo hora con soloHora. */
export const cuando = (iso: string | null | undefined, soloHora = false): string => {
	if (!iso) return "";
	const d = new Date(iso);
	if (isNaN(d.getTime())) return String(iso);
	const h = d.getHours();
	const hora = `${h % 12 === 0 ? 12 : h % 12}:${dos(d.getMinutes())} ${h < 12 ? "am" : "pm"}`;
	return soloHora ? hora : `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${d.getFullYear()} ${hora}`;
};
/** "29/11" (día y mes en hora del navegador). */
export const diaMes = (iso: string | null | undefined): string => {
	if (!iso) return "";
	const d = new Date(iso);
	return isNaN(d.getTime()) ? "" : `${dos(d.getDate())}/${dos(d.getMonth() + 1)}`;
};

/** "2026-10-12 15:00:00" <-> valor de <input type="datetime-local">. */
export const aInput = (dt: string | null | undefined): string => (dt ? String(dt).substring(0, 16).replace(" ", "T") : "");
export const deInput = (v: string): string | null => (v ? v.replace("T", " ") + (v.length === 16 ? ":00" : "") : null);

export const normalizar = (t: any): string => String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
export const numOrNull = (v: string): number | null => (v.trim() === "" || isNaN(Number(v)) ? null : Number(v));
export const slug = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");

/** Los PDF llegan como Blob; un 422 también, con el mensaje adentro. */
export const blobErrorText = async (e: any): Promise<string> => {
	const data = e?.response?.data;
	if (data instanceof Blob) {
		try { const j = JSON.parse(await data.text()); return j.message || "No se pudo descargar."; } catch { return "No se pudo descargar."; }
	}
	return e?.response?.data?.message || "No se pudo descargar.";
};

export const guardarBlob = (blob: Blob, nombre: string) => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url; a.download = nombre; document.body.appendChild(a); a.click(); a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 4000);
};

/** Pastilla al estilo de Descuentos: neutral (filo), acento u oro (lo pendiente). Nunca verde. */
export const Chip: React.FC<{ tono?: "filo" | "acento" | "oro"; children: React.ReactNode; title?: string }> = ({ tono = "filo", children, title }) => (
	<span className={"wevChip " + tono} title={title}>{children}</span>
);

/** Ventana modal con el estilo de Actualizaciones (formularios cortos). */
export const Modal: React.FC<{ title: string; onClose: () => void; ancho?: number; children: React.ReactNode }> = ({ title, onClose, ancho, children }) => (
	<div className="wau-modal-fondo" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
		<div className="wau-modal" style={ancho ? { maxWidth: ancho } : undefined} onClick={e => e.stopPropagation()}>
			<div className="wev-modal-cab"><h3>{title}</h3>
				<button type="button" className="wev-x" aria-label="Cerrar" title="Cerrar" onClick={onClose}>×</button></div>
			{children}
		</div>
	</div>
);
